import { useEffect, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useParams } from "react-router-dom";
import {
  archiveModule,
  emptyContent,
  fetchPreviewContext,
  fetchVariables,
  getModule,
  publishModule,
  updateModule,
  type ContentData,
  type ContentType,
  type LocalizedContent,
  type TitleMap,
} from "@/api/content";
import { Badge, Button, Card } from "@/components";
import { useAuthStore } from "@/state/auth.store";
import { useLang } from "@/i18n/LanguageContext";
import { ModuleContent } from "@/features/program/content/ModuleContent";
import { CONTENT_TYPE_OPTIONS, STATUS_TONE, contentTypeHelp } from "./contentMeta";
import { personalizeData } from "./renderTemplate";
import { ContentEditor } from "./editors/ContentEditor";
import { QuizEditor } from "./QuizEditor";

const STATUS: Record<string, { nl: string; en: string }> = {
  draft: { nl: "concept", en: "draft" },
  published: { nl: "gepubliceerd", en: "published" },
  archived: { nl: "gearchiveerd", en: "archived" },
};

const LANGS = ["nl", "en"] as const;
type Lang = (typeof LANGS)[number];
const LANG_LABEL: Record<Lang, string> = { nl: "NL", en: "EN" };

export function ModuleEditorPage() {
  const { id = "" } = useParams();
  const qc = useQueryClient();
  const { t } = useLang();
  const canEdit = useAuthStore((s) => s.user?.role) === "org_admin";

  const detail = useQuery({ queryKey: ["module", id], queryFn: ({ signal }) => getModule(id, signal) });
  const variables = useQuery({ queryKey: ["content-variables"], queryFn: ({ signal }) => fetchVariables(signal) });
  const previewCtx = useQuery({ queryKey: ["preview-context"], queryFn: ({ signal }) => fetchPreviewContext(signal) });

  const [titles, setTitles] = useState<TitleMap>({ nl: "" });
  const [contentType, setContentType] = useState<ContentType>("document");
  const [requiresAck, setRequiresAck] = useState(false);
  const [content, setContent] = useState<LocalizedContent>({ nl: {} });
  const [bilingual, setBilingual] = useState(false);
  const [editLang, setEditLang] = useState<Lang>("nl");
  const [formKey, setFormKey] = useState("");
  const [error, setError] = useState<string | null>(null);
  const snapshot = useRef("");

  useEffect(() => {
    if (detail.data) {
      const ti = detail.data.title_i18n && Object.keys(detail.data.title_i18n).length ? detail.data.title_i18n : { nl: detail.data.title };
      const c = detail.data.editable_content && Object.keys(detail.data.editable_content).length ? detail.data.editable_content : { nl: emptyContent(detail.data.content_type) };
      const bi = "en" in c || "en" in ti;
      setTitles(ti);
      setContentType(detail.data.content_type);
      setRequiresAck(detail.data.requires_acknowledgement);
      setContent(c);
      setBilingual(bi);
      setEditLang("nl");
      setFormKey(`${detail.data.id}:${detail.data.updated_at}`);
      snapshot.current = JSON.stringify({ titles: ti, contentType: detail.data.content_type, requiresAck: detail.data.requires_acknowledgement, content: c });
    }
  }, [detail.data?.id, detail.data?.updated_at]); // eslint-disable-line react-hooks/exhaustive-deps

  const formSig = JSON.stringify({ titles, contentType, requiresAck, content });
  const dirty = !!formKey && formSig !== snapshot.current;

  useEffect(() => {
    function handler(e: BeforeUnloadEvent) {
      if (dirty) {
        e.preventDefault();
        e.returnValue = "";
      }
    }
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [dirty]);

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ["module", id] });
    qc.invalidateQueries({ queryKey: ["modules"] });
  };

  function buildPayload() {
    const langs: Lang[] = bilingual ? ["nl", "en"] : ["nl"];
    const title_i18n: TitleMap = {};
    const payloadContent: LocalizedContent = {};
    for (const l of langs) {
      title_i18n[l] = (titles[l] || "").trim() || titles.nl || "";
      payloadContent[l] = content[l] ?? emptyContent(contentType);
    }
    return { title: title_i18n.nl || titles.nl || "", title_i18n, content_type: contentType, requires_acknowledgement: requiresAck, content: payloadContent };
  }

  const save = useMutation({ mutationFn: () => updateModule(id, buildPayload()), onSuccess: invalidate });
  const publish = useMutation({ mutationFn: () => publishModule(id), onSuccess: invalidate });
  const archive = useMutation({ mutationFn: () => archiveModule(id), onSuccess: invalidate });

  async function onSave() {
    setError(null);
    try {
      await save.mutateAsync();
    } catch (e) {
      setError(e instanceof Error ? e.message : t("Opslaan mislukt", "Could not save"));
    }
  }

  async function onPublish() {
    setError(null);
    try {
      if (dirty) await save.mutateAsync();
      await publish.mutateAsync();
    } catch (e) {
      setError(e instanceof Error ? e.message : t("Publiceren mislukt", "Could not publish"));
    }
  }

  function onTypeChange(next: ContentType) {
    setContentType(next);
    const reset: LocalizedContent = {};
    (bilingual ? LANGS : (["nl"] as Lang[])).forEach((l) => (reset[l] = emptyContent(next)));
    setContent(reset);
  }

  function toggleBilingual(on: boolean) {
    setBilingual(on);
    if (on) {
      setContent((c) => ({ nl: c.nl ?? emptyContent(contentType), en: c.en ?? emptyContent(contentType) }));
      setTitles((tt) => ({ nl: tt.nl ?? "", en: tt.en ?? "" }));
    } else {
      setContent((c) => ({ nl: c.nl ?? emptyContent(contentType) }));
      setTitles((tt) => ({ nl: tt.nl ?? "" }));
      setEditLang("nl");
    }
  }

  if (detail.isLoading) return <div className="wrap" style={{ padding: 40 }}>{t("Laden…", "Loading…")}</div>;
  if (detail.isError || !detail.data) return <div className="wrap" style={{ padding: 40 }}>{t("Module niet gevonden.", "Module not found.")}</div>;
  const m = detail.data;
  const ctx = previewCtx.data ?? {};
  const statusLabel = STATUS[m.status] ? t(STATUS[m.status].nl, STATUS[m.status].en) : m.status;
  const langContent = content[editLang] ?? emptyContent(contentType);

  return (
    <section>
      <div className="wrap">
        <div className="toolbar">
          <div>
            <Link to="/app/content" className="eyebrow" style={{ marginBottom: 6 }}>← {t("Modules", "Modules")}</Link>
            <h2 style={{ fontSize: 28 }}>{m.title}</h2>
          </div>
          <div className="spacer" />
          <Badge tone={STATUS_TONE[m.status]}>{statusLabel}</Badge>
          {dirty && <Badge tone="warn">{t("niet opgeslagen", "unsaved")}</Badge>}
          {!dirty && m.has_unpublished_changes && <Badge tone="warn">{t("concept klaar om te publiceren", "draft ready to publish")}</Badge>}
          {m.requires_acknowledgement && <Badge>{t("akkoord vereist", "sign-off required")}</Badge>}
        </div>

        {error && <div className="form-error">{error}</div>}

        {canEdit && (
          <div className="toolbar">
            <Button onClick={onSave} disabled={!dirty || save.isPending}>
              {save.isPending ? t("Opslaan…", "Saving…") : dirty ? t("Opslaan •", "Save •") : t("Opgeslagen", "Saved")}
            </Button>
            <Button variant="outline" onClick={onPublish} disabled={(!dirty && !m.has_unpublished_changes) || publish.isPending || save.isPending}>
              {publish.isPending ? t("Publiceren…", "Publishing…") : t("Publiceren", "Publish")}
            </Button>
            <div className="spacer" />
            {m.status !== "archived" && (
              <Button variant="outline" onClick={() => archive.mutate()} disabled={archive.isPending}>{t("Archiveren", "Archive")}</Button>
            )}
          </div>
        )}

        <div className="editor-grid">
          <Card>
            <p className="card-num">{t("BEWERKEN", "EDIT")}</p>
            {canEdit ? (
              <>
                <label style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 12 }}>
                  <input type="checkbox" checked={bilingual} onChange={(e) => toggleBilingual(e.target.checked)} />
                  {t("Tweetalig invullen (NL + EN)", "Fill in two languages (NL + EN)")}
                </label>

                {bilingual && (
                  <div className="lang-tabs">
                    {LANGS.map((l) => (
                      <button key={l} type="button" className={`lang-tab ${editLang === l ? "active" : ""}`} onClick={() => setEditLang(l)}>
                        {LANG_LABEL[l]}
                      </button>
                    ))}
                  </div>
                )}

                <div className="field">
                  <label htmlFor="title">{t("Titel", "Title")}{bilingual ? ` (${LANG_LABEL[editLang]})` : ""}</label>
                  <input id="title" value={titles[editLang] ?? ""} onChange={(e) => setTitles((tt) => ({ ...tt, [editLang]: e.target.value }))} />
                </div>
                <div className="field">
                  <label htmlFor="ctype">{t("Type", "Type")}</label>
                  <select id="ctype" value={contentType} onChange={(e) => onTypeChange(e.target.value as ContentType)}>
                    {CONTENT_TYPE_OPTIONS.map((o) => (
                      <option key={o.value} value={o.value}>{t(o.nl, o.en)}</option>
                    ))}
                  </select>
                  <span style={{ fontSize: 12.5, color: "var(--grey-text)", marginTop: 6 }}>{contentTypeHelp(contentType, t)}</span>
                </div>
                <label style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 14 }}>
                  <input type="checkbox" checked={requiresAck} onChange={(e) => setRequiresAck(e.target.checked)} />
                  {t("Akkoord vereist", "Sign-off required")}
                </label>

                {formKey && (
                  <ContentEditor
                    key={`${formKey}:${editLang}`}
                    type={contentType}
                    moduleId={id}
                    content={langContent}
                    onChange={(c: ContentData) => setContent((prev) => ({ ...prev, [editLang]: c }))}
                    variables={variables.data ?? []}
                  />
                )}
              </>
            ) : (
              <p className="no-data-message">{t("Alleen-lezen. Bewerken vereist de rol org-beheerder.", "Read-only. Editing requires the org-admin role.")}</p>
            )}
          </Card>

          <Card>
            <p className="card-num">{t("PREVIEW", "PREVIEW")} {bilingual ? `· ${LANG_LABEL[editLang]}` : ""}</p>
            <p style={{ color: "var(--grey-text)", fontSize: 13, marginBottom: 12 }}>
              {t("Variabelen ingevuld met jouw gegevens als voorbeeld.", "Variables filled with your details as an example.")}
            </p>
            <ModuleContent type={contentType} content={personalizeData(langContent, ctx)} />
          </Card>
        </div>

        <Card>
          <p className="card-num">{t("VERSIES", "VERSIONS")}</p>
          <ul className="list-rows">
            {m.versions.map((v) => (
              <li key={v.id}>
                <span><strong>v{v.version_no}</strong> · {new Date(v.created_at).toLocaleString()}</span>
                <Badge tone={v.is_published ? "ok" : "warn"}>{v.is_published ? t("gepubliceerd", "published") : t("concept", "draft")}</Badge>
              </li>
            ))}
          </ul>
        </Card>

        {canEdit && <QuizEditor moduleId={m.id} />}
      </div>
    </section>
  );
}
